import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
export interface Contact {
    id: number;
    user_id: number;
    first_name: string;
    last_name: string;
    suffix: string | null;
    email: string;
    phone: string;
    created_at: string;
    updated_at: string;
    deleted_at: string | null;
}

export interface EventAddress {
    address: string;
    lat: number;
    lng: number;
}

export interface EventTextStyle {
    color: string;
    font_size: string;
    font_style: {
        textBold: boolean;
        textItalic: boolean;
        textUnderline: boolean;
    };
    text_align: string;
    font_family: string;
}

export interface EventCreator {
    id: number;
    name: string;
    email: string;
    phone: string;
}

export interface EventData {
    event_id: number;
    status: number;
    name: string;
    host_name: string;
    orientation: string;
    event_created_date: string;
    event_created_time: string;
    due_date: string | null;
    address: EventAddress;
    venue: string;
    dress_code: string | null;
    text_style: EventTextStyle;
    text_position: string;
    background_color: boolean | string;
    pattern: string;
    pattern_id: number;
    logo: boolean | string;
    logo_id: boolean | number;
    logo_position: boolean | string;
    border: boolean | string;
    border_id: boolean | number;
    opacity: boolean | string;
    count_all_contacts: number;
    count_contacts_ignore: number;
    count_contacts_going: number;
    count_contacts_decline: number;
    count_contacts_late: number;
    count_contacts_visited: number;
    count_contacts_failed: number;
    count_contacts_scanned: number;
    count_contacts_not_paid: number;
    count_contacts_delivered: number;
    count_contacts_read: number;
    count_contacts_blocked: number;
    count_contacts_unsubscribed: number;
    count_contacts_qr_code_delivered: number | null;
    count_contacts_qr_code_not_delivered: number | null;
    count_whatsapp_invitations_unsent: number;
    count_whatsapp_invitations_sent: number;
    count_whatsapp_invitations_delivered: number;
    count_whatsapp_invitations_received: number;
    count_whatsapp_invitations_read: number;
    count_whatsapp_invitations_failed: number;
    count_whatsapp_invitations_optin_delivered: number;
    count_whatsapp_invitations_no_whatsapp: number;
    count_qrcode_unsent: number;
    count_qrcode_sent: number;
    count_qrcode_delivered: number;
    count_qrcode_received: number;
    count_qrcode_read: number;
    count_qrcode_failed: number;
    count_messages_received: number;
    password: string;
    hash: string;
    simplified: boolean;
    price: number | null;
    price_unPaid: number | null;
    price_paid: number;
    header: string | null;
    footer: string | null;
    language: string;
    paidInvitations: number;
    unPaidInvitations: number;
    is_reminder_active: boolean;
    can_send_invitations: boolean;
    can_send_undelivered_qr_codes: boolean;
    invitation_show_qr_code: number;
    qr_code_design_id: number;
    type: number;
    invitation_template_en_body_text: string | null;
    invitation_template_en_accept_button_text: string | null;
    invitation_template_en_decline_button_text: string | null;
    invitation_template_en_location_button_text: string | null;
    invitation_template_ar_body_text: string | null;
    invitation_template_ar_accept_button_text: string | null;
    invitation_template_ar_decline_button_text: string | null;
    invitation_template_ar_location_button_text: string | null;
    reminder_remind_only_template_en_body_text: string | null;
    reminder_remind_only_template_en_location_button_text: string | null;
    reminder_remind_only_template_ar_body_text: string | null;
    reminder_remind_only_template_ar_location_button_text: string | null;
    reminder_accept_decline_template_en_body_text: string | null;
    reminder_accept_decline_template_en_accept_button_text: string | null;
    reminder_accept_decline_template_en_decline_button_text: string | null;
    reminder_accept_decline_template_en_location_button_text: string | null;
    reminder_accept_decline_template_ar_body_text: string | null;
    reminder_accept_decline_template_ar_accept_button_text: string | null;
    reminder_accept_decline_template_ar_decline_button_text: string | null;
    reminder_accept_decline_template_ar_location_button_text: string | null;
    allow_to_add_invitation_to_wallets: number;
    start_datetime: string;
    open_event_hash: string | null;
    open_event_page_total_visits: number;
    open_event_page_unique_visits: number;
    is_open_event: number;
    pov_enabled: number;
    label: string;
    source: string;
    isPremium: number;
    description?: string;
    additional_info?: string;
    event_creator: EventCreator;
}

export interface EventContact {
    actual_additional_guests?: number;
    quantity_members?: number;
}

export interface EventDetailApiResponse {
    data: {
        contact: Contact;
        event_contact?: EventContact;
        status: number;
        message: string | null;
        hash: string;
        people_going: number;
        victor: number;
        quantity_members?: number;
        actual_additional_guests?: number;
        event: {
            data: EventData[];
        };
    };
}

export function useInvitationDetails(eventId: string | undefined) {
    const [eventData, setEventData] = useState<EventDetailApiResponse | null>(null);
    const [eventQrcode, setEventQrcode] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const [qrcodeLoading, setQrcodeLoading] = useState(false);
    const BASE = import.meta.env.VITE_REACT_APP_API_BASE_URL;

    const fetchEventDetails = useCallback(async () => {
        if (!eventId) return;
        setIsLoading(true);
        setIsError(false);
        setErrorMessage(null);
        try {
            const response = await axios.get<EventDetailApiResponse>(`${BASE}/events/invitation/${eventId}`);
            if (response.status === 200) {
                setEventData(response.data);
                setErrorMessage(null);
            } else {
                setIsError(true);
                setErrorMessage('An error occurred while fetching event details.');
            }
        } catch (error: any) {
            setIsError(true);
            if (error.response?.status === 422) {
                setErrorMessage('Invitation not found');
            } else if (error.response?.data?.message) {
                setErrorMessage(error.response.data.message);
            } else {
                setErrorMessage('An unexpected error occurred.');
            }
        } finally {
            setIsLoading(false);
        }
    }, [eventId]);

    const fetchEventQrcode = useCallback(async () => {
        if (!eventId) return;
        setQrcodeLoading(true); // Start QR code loading
        try {
            const response = await axios.get(
                `${BASE}/events/qrcode-whatsapp/${eventId}`,
                { responseType: 'arraybuffer' }
            );
            if (response.status === 200 && response.data) {
                const blob = new Blob([response.data], { type: 'image/png' });
                const reader = new FileReader();
                reader.onloadend = () => {
                    const result = reader.result as string;
                    const base64 = result.split(',')[1];
                    setEventQrcode(base64);
                    setQrcodeLoading(false); // Done loading
                };
                reader.readAsDataURL(blob);
            } else {
                setEventQrcode(null);
                setQrcodeLoading(false);
            }
        } catch (error) {
            setEventQrcode(null);
            setQrcodeLoading(false);
        }
    }, [eventId]);

    const updateEventDetails = useCallback(async () => {
        if (!eventId) return;
        try {
            const response = await axios.get<EventDetailApiResponse>(`${BASE}/events/invitation/${eventId}`);
            if (response.status === 200) {
                setEventData(response.data);
                setErrorMessage(null);
            }
        } catch (error: any) {
            // Don't set error state for background updates
            console.error('Failed to update event details:', error);
        }
    }, [eventId, BASE]);

    useEffect(() => {
        fetchEventDetails();
    }, [fetchEventDetails]);

    useEffect(() => {
        if (eventData?.data?.status === 1) {
            fetchEventQrcode();
        }
    }, [eventData, fetchEventQrcode]);

    const handleResponse = async (status: number, count?: number) => {
        setActionLoading(true);
        setActionError(null);
        try {
            await axios.post(`${BASE}/events/invitation`, {
                hash: eventData?.data?.hash || eventId,
                status,
                amount_of_guests: count || 0,
            });
            updateEventDetails();
        } catch (error: any) {
            if (error.response?.data?.message) {
                setActionError(error.response.data.message);
            } else {
                setActionError('Failed to update attendance.');
            }
        } finally {
            setActionLoading(false);
        }
    };

    const handleDecline = async (declineMessage: string) => {
        setActionLoading(true);
        setActionError(null);
        try {
            await axios.post(`${BASE}/events/invitation`, {
                hash: eventData?.data?.hash || eventId,
                status: 2,
            });

            const eventObj = eventData?.data?.event?.data?.[0];
            if (declineMessage && eventData?.data?.contact?.id && eventObj?.event_id) {
                await axios.post(`${BASE}/events/message`, {
                    message: declineMessage,
                    sender: eventData.data.contact.id,
                    event: eventObj.event_id,
                });
            }
            updateEventDetails();
        } catch (error: any) {
            if (error.response?.data?.message) {
                setActionError(error.response.data.message);
            } else {
                setActionError('Failed to decline invitation.');
            }
        } finally {
            setActionLoading(false);
        }
    };

    return {
        eventData,
        eventQrcode,
        isLoading,
        isError,
        errorMessage,
        actionLoading,
        actionError,
        setActionError,
        setActionLoading,
        fetchEventDetails,
        handleResponse,
        handleDecline,
        qrcodeLoading, // <-- export loading state
    };
}